import { Injectable } from '@angular/core';
import {
  isAndroidBrowser,
  isCapacitorNative,
  isPlatformIOS,
} from '../environments/environment-helpers';
import { LoggingService } from './logging.service';
import { TelemetryService } from './telemetry.service';
import {
  InstallPlatform,
  InstallTarget,
  InstallTargetKind,
} from './app-install-types';
import {
  readStartupSheetOptOut,
  resolveInstallTargetFromInputs,
  shouldShowStartupSheetFromInputs,
  writeStartupSheetOptOut,
} from './app-install-resolver';
import {
  androidPlayStoreUrlForEnv,
  IOS_APP_STORE_URL,
} from '@cf-shared/data-model/app-install-urls';
import { AppEnvironment } from '../environments/environment';

export { InstallTargetKind, InstallTarget, InstallPlatform };

export type AppInstallSurface = 'settings-button' | 'startup-sheet-impression' | 'sheet-cta';
export type AppInstallTriggerSource = 'startup' | 'settings';
export type AppInstallCta = 'store-badge' | 'open-link' | 'pwa-prompt' | 'dont-show-again' | null;

// The subset of InstallTarget that's allowed to drive an app_install event —
// excludes None (which represents a non-visible surface and would emit a
// silently-misleading event). Callers must guard before invoking
// trackAppInstallEvent; the type narrowing makes that a compile-time
// requirement.
export type VisibleInstallTarget = Exclude<InstallTarget, { kind: InstallTargetKind.None }>;

// Resolves the InstallTarget for the current platform/runtime, fronts
// navigator.getInstalledRelatedApps (cached per session — the installed-state
// cannot change at runtime), and centralizes app_install telemetry so both
// emission sites (Settings page + sheet) share one prop-shape contract.
// Resolution logic is delegated to the pure resolveInstallTargetFromInputs
// helper so the table is unit-testable without touching Angular / Capacitor.
@Injectable({ providedIn: 'root' })
export class AppInstallService {

  private installedCheck?: Promise<boolean>;
  private badgesPreloaded = false;

  constructor(
    private telemetry: TelemetryService,
    private logging: LoggingService,
  ) {}

  async resolveInstallTarget(): Promise<InstallTarget> {
    // kick off store-badge SVG preload as early as possible so the bytes are
    // cached by the time the popover slide-up finishes; resolveInstallTarget
    // is the common path both surfaces (startup sheet + Settings button) go
    // through before showing the sheet
    this.preloadStoreBadges();

    const hasAndroidNativeInstalled = isAndroidBrowser()
      ? await this.hasAndroidNativeInstalled()
      : false;
    return resolveInstallTargetFromInputs({
      isCapacitorNative: isCapacitorNative(),
      isPlatformIOS: isPlatformIOS(),
      isAndroidBrowser: isAndroidBrowser(),
      hasAndroidNativeInstalled,
      hasPwaPrompt: typeof window['startInstallPrompt'] === 'function',
      universalLinkUrl: location.origin + '/',
      // env-aware: dev bundle recommends the dev Play listing so install-
      // detection can be validated on dev.mh-physique.app with the dev APK
      androidStoreUrl: androidPlayStoreUrlForEnv(AppEnvironment.name),
      iosStoreUrl: IOS_APP_STORE_URL,
    });
  }

  // the rule lives in shouldShowStartupSheetFromInputs; this only gathers
  // the platform flags and this browser's "Don't show again" choice (#5707)
  shouldShowStartupSheet(): boolean {
    return shouldShowStartupSheetFromInputs({
      isCapacitorNative: isCapacitorNative(),
      isPlatformIOS: isPlatformIOS(),
      isAndroidBrowser: isAndroidBrowser(),
      isOptedOut: () => this.isStartupSheetOptedOut(),
    });
  }

  // records the startup sheet's "Don't show again" tap for this browser.
  // if storage refuses the write, the sheet still closes; the failure is
  // reported so a browser that keeps showing it after an opt-out is visible
  optOutOfStartupSheet(): void {
    try {
      writeStartupSheetOptOut(localStorage);
    } catch (err) {
      this.logging.trackSilentProblem('app-install.opt-out-write-failed', {
        message: (err as Error)?.message,
      });
    }
  }

  // if storage cannot be read, fall back to showing the sheet (today's
  // behaviour) and report it
  private isStartupSheetOptedOut(): boolean {
    try {
      return readStartupSheetOptOut(localStorage);
    } catch (err) {
      this.logging.trackSilentProblem('app-install.opt-out-read-failed', {
        message: (err as Error)?.message,
      });
      return false;
    }
  }

  runPwaPrompt(): void {
    const prompt = window['startInstallPrompt'];
    if (typeof prompt === 'function') prompt();
  }

  // Single source of truth for app_install events. Both the Settings page and
  // the bottom-sheet component call this so the prop shape (and the
  // installed_detected derivation) stays consistent. See
  // design-docs/install-affordances.md §"Telemetry shape".
  trackAppInstallEvent(
    surface: AppInstallSurface,
    triggerSource: AppInstallTriggerSource,
    target: VisibleInstallTarget,
    cta: AppInstallCta,
  ): void {
    this.telemetry.trackEvent('app_install', {
      surface,
      trigger_source: triggerSource,
      target: telemetryTargetKind(target),
      platform: this.resolvePlatform(),
      cta,
      installed_detected: this.installedDetectedFor(target),
    });
  }

  // Cached for the session — getInstalledRelatedApps cannot change at runtime,
  // and both the startup sheet + Settings button consume it.
  private hasAndroidNativeInstalled(): Promise<boolean> {
    if (this.installedCheck) return this.installedCheck;
    this.installedCheck = this.computeAndroidNativeInstalled();
    return this.installedCheck;
  }

  private async computeAndroidNativeInstalled(): Promise<boolean> {
    const nav = navigator as Navigator & {
      getInstalledRelatedApps?: () => Promise<Array<{ platform: string; id?: string }>>;
    };
    // not exposed on Firefox / Samsung Internet / older Chromium
    if (typeof nav.getInstalledRelatedApps !== 'function') return false;
    try {
      const apps = await nav.getInstalledRelatedApps();
      return apps.length > 0;
    } catch (err) {
      // The typeof check above already handles "API not implemented"; a thrown
      // rejection here is unexpected and must surface so we can spot Chromium
      // regressions or permission-policy changes that break the gate.
      this.logging.trackSilentProblem('app-install.related-apps-failed', {
        message: (err as Error)?.message,
      });
      return false;
    }
  }

  // Warm the browser cache for the two store-badge SVGs the sheet renders.
  // Using new Image() (instead of <link rel="preload">) keeps the preload
  // scoped to the few users who actually trigger the sheet — desktop users
  // and Capacitor-native users never call resolveInstallTarget so this stays
  // dormant for them.
  private preloadStoreBadges(): void {
    if (this.badgesPreloaded) return;
    this.badgesPreloaded = true;
    new Image().src = 'assets/store-badges/apple-download-en-us.svg';
    new Image().src = 'assets/store-badges/google-play-en-us.svg';
  }

  // Analytics platform discriminator. Exposed so emission sites label their
  // app_install events with one source of truth (no per-call branching).
  resolvePlatform(): InstallPlatform {
    if (isPlatformIOS() && !isCapacitorNative()) return 'ios';
    if (isAndroidBrowser()) return 'android';
    return 'desktop';
  }

  // installed_detected per design doc: true only when getInstalledRelatedApps
  // confirmed the Android native app (target=NativeOpen), false on
  // Android-not-installed (target=NativeStore on android), null on iOS /
  // desktop (no detection API).
  private installedDetectedFor(target: VisibleInstallTarget): boolean | null {
    if (this.resolvePlatform() !== 'android') return null;
    return target.kind === InstallTargetKind.NativeOpen;
  }
}

// Pure mapping from VisibleInstallTarget to the telemetry discriminator
// string. Kept as a module-level function (not a method) so it's trivially
// testable and has no implicit `this` dependency. None is excluded at the
// type level — see VisibleInstallTarget above.
function telemetryTargetKind(target: VisibleInstallTarget): 'native-store' | 'native-open' | 'pwa-prompt' {
  switch (target.kind) {
    case InstallTargetKind.NativeOpen: return 'native-open';
    case InstallTargetKind.PwaPrompt:  return 'pwa-prompt';
    case InstallTargetKind.NativeStore: return 'native-store';
  }
}
